import React from 'react';
import { FormattedMessage } from 'react-intl';

import { ROUTES } from 'Constants';
import { Colors, Display } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { OverbookingInsightDetails } from './OverbookingInsightDetails';
import { OverbookingWarningDto } from './OverbookingWarningDto';

const formattedMessages = {
	modalTitle: 'reservation__overbooking-modal__title',
	currentCapacityLabel: 'reservation__overbooking-modal__current-capacity',
	newCapacityLabel: 'reservation__overbooking-modal__new-capacity',
	capacityAfterBookingLabel: 'reservation__overbooking-modal__capacity-after-boooking-label',
} as const;

interface OverbookingWithInsightModalProps {
	showModal: boolean;
	actionText: React.ReactNode;
	departingOverbookingWarnings?: OverbookingWarningDto[];
	returningOverbookingWarnings?: OverbookingWarningDto[];
	onClickCross?: () => void;
	onClickSelect?: () => void;
}

export default function OverbookingWithInsightModal({
	showModal,
	actionText,
	departingOverbookingWarnings,
	returningOverbookingWarnings,
	onClickSelect,
	onClickCross,
}: OverbookingWithInsightModalProps) {
	return (
		<NewModal isOpen={showModal} label="Overbooked">
			<NewModal.Header>
				<NewModal.Title>
					<FormattedMessage
						id={formattedMessages.modalTitle}
						defaultMessage="Confirm overbooking allowance"
					/>
				</NewModal.Title>
			</NewModal.Header>
			<NewModal.Body>
				{departingOverbookingWarnings && (
					<OverbookingJourneySection
						title={
							<FormattedMessage
								id="reservation__overbooking-modal__departing-trip-title"
								defaultMessage="Departing trip"
							/>
						}
						overbookingWarnings={departingOverbookingWarnings}
					/>
				)}
				{returningOverbookingWarnings && (
					<OverbookingJourneySection
						title={
							<FormattedMessage
								id="reservation__overbooking-modal__returning-trip-title"
								defaultMessage="Returning trip"
							/>
						}
						overbookingWarnings={returningOverbookingWarnings}
					/>
				)}
			</NewModal.Body>
			<NewModal.Footer>
				<NewModal.CancelButton
					display={Display.Outline}
					colors={Colors.Grey}
					onClick={() => {
						if (onClickCross) {
							onClickCross();
						}
					}}
				>
					Back
				</NewModal.CancelButton>
				<NewModal.ConfirmButton
					display={Display.Solid}
					colors={Colors.Secondary}
					onClick={() => {
						if (onClickSelect) {
							onClickSelect();
						}
					}}
				>
					{actionText}
				</NewModal.ConfirmButton>
			</NewModal.Footer>
		</NewModal>
	);
}

interface OverbookingJourneySectionProps {
	title: React.ReactNode;
	overbookingWarnings: OverbookingWarningDto[];
}

export function OverbookingJourneySection({ title, overbookingWarnings }: OverbookingJourneySectionProps) {
	if (overbookingWarnings.length < 1) {
		return <></>;
	}

	const { tripId } = overbookingWarnings[0];

	return (
		<div>
			<NewModal.Label>
				{title} -{' '}
				<a target="_blank" rel="noreferrer" href={ROUTES.FERRY_SCHEDULE_EDIT.url(tripId, 'insights')}>
					see details
				</a>
			</NewModal.Label>
			{overbookingWarnings.map(warning => {
				return (
					<OverbookingJourneyInsight
						key={`${warning.overbookingCause}-${warning.capacityType}`}
						overbookingWarning={warning}
					/>
				);
			})}
		</div>
	);
}

interface OverbookingJourneyInsightProps {
	overbookingWarning: OverbookingWarningDto;
}

export function OverbookingJourneyInsight({ overbookingWarning }: OverbookingJourneyInsightProps) {
	return (
		<NewModal.Section>
			<OverbookingInsightDetails overbookingWarning={overbookingWarning} />
		</NewModal.Section>
	);
}
